import { useEffect, useMemo, useRef } from "react";
import type { Types } from "@cornerstonejs/tools";
import { getCornerstone, getCornerstoneTools } from "@/utils/dicomLoader";
import { loadMaskAsContours } from "@/utils/niftiMaskLoader";
import { savedStoneOrder } from "@/utils/savedStoneOrder";
import type { SavedStoneOrder } from "@/utils/savedStoneOrder";
import { SavedStonesOverlapError } from "@/utils/stackSliceGeometry";
import { getContourSegmentationId } from "@/utils/viewportToolSetup";
import { emit } from "@/lib/viewerBus";
import { useAtomValue } from "jotai";
import { studyMetadataAtom } from "@/atoms/selectionAtoms";
import type { StudyMetadataResponse } from "@/utils/dicomMetadataLoader";
import { logger } from "@/utils/logger";

type SavedMaskResponse = {
  mask_url?: string | null;
  annotations: Array<{
    annotation_id: string;
    segment_index: number;
    series_instance_uid: string;
    attributes?: Record<string, unknown>;
    bounding_box?: Record<string, number> | null;
    companion_annotations?: Array<{ companion_id: string; series_instance_uid: string }>;
  }>;
  companion_mask_urls?: Record<string, string> | null;
};
/** `order`: the slice order the stone was saved in; a companion takes its primary's. */
type Target = { id: string; index: number; key: string; order: SavedStoneOrder };
type Job = { url: string; series: string; targets: Target[]; parent?: string };
type SavedContourAnnotation = Types.ContourSegmentationAnnotation & {
  data: Types.ContourSegmentationAnnotation["data"] & {
    savedAnnotationId?: unknown;
    segmentation?: { segmentationId?: string };
  };
};

export function jobsFor(data?: SavedMaskResponse): Job[] {
  const primary = new Map<string, Job>();
  const companions: Job[] = [];
  for (const ann of data?.annotations ?? []) {
    const series = ann.series_instance_uid?.split("::")[0];
    if (!series || !data?.mask_url) continue;
    const key = JSON.stringify([ann.annotation_id, series, "primary"]);
    let job = primary.get(series);
    if (!job) {
      job = { url: data.mask_url, series, targets: [] };
      primary.set(series, job);
    }
    const order = savedStoneOrder(ann);
    job.targets.push({ id: ann.annotation_id, index: ann.segment_index, key, order });
    for (const comp of ann.companion_annotations ?? []) {
      const url = data.companion_mask_urls?.[comp.companion_id];
      const compSeries = comp.series_instance_uid?.split("::")[0];
      if (!url || !compSeries) continue;
      companions.push({
        url,
        series: compSeries,
        parent: key,
        targets: [
          {
            id: ann.annotation_id,
            index: ann.segment_index,
            key: JSON.stringify([ann.annotation_id, compSeries, comp.companion_id]),
            order,
          },
        ],
      });
    }
  }
  return [...primary.values(), ...companions];
}

/**
 * How the "saved mask is on a series that isn't open" toast names a series:
 * "Series 5 (AX ST)" when the study metadata knows it, else the UID tail.
 * A missing Series Number is stored as 0, so it is left out (ENG-248).
 */
export function savedMaskSeriesLabel(
  seriesUid: string,
  metadata: Pick<StudyMetadataResponse, "series_metadata"> | null | undefined,
): string {
  const meta = metadata?.series_metadata?.[seriesUid];
  const number = meta && meta.series_number > 0 ? `Series ${meta.series_number}` : null;
  const name = meta?.series_description?.trim() || null;
  if (number && name) return `${number} (${name})`;
  return number ?? name ?? `series …${seriesUid.slice(-6)}`;
}

/** Toast for a failed load. Overlapping stones are in the file, so a reload can't help (ENG-258). */
export function savedMaskLoadFailureMessage(error: unknown): string {
  return error instanceof SavedStonesOverlapError
    ? "Two saved stones overlap on the same pixels, so this mask can't be shown. Please report this case."
    : "Could not load the saved mask. Please reload the case.";
}

/** Successful loads are tracked by annotation AND series/companion, within one case volume. */
export function useSavedMaskContourLoading({
  enabled,
  active,
  ready,
  scope,
  volumeId,
  data,
  revision,
  contourRevision,
  prepareIndices,
  onLoaded,
}: {
  enabled: boolean;
  active: boolean;
  ready: boolean;
  scope: string;
  volumeId: string | null;
  data?: SavedMaskResponse;
  revision?: number;
  contourRevision?: number;
  prepareIndices: () => ReadonlyMap<number, number>;
  onLoaded: () => void;
}) {
  // Read only when a toast is built; a ref keeps metadata updates from
  // restarting the load effect.
  const studyMetadata = useAtomValue(studyMetadataAtom);
  const studyMetadataRef = useRef(studyMetadata);
  studyMetadataRef.current = studyMetadata;
  const completed = useMemo(() => new Set<string>(), [scope, volumeId, enabled]);
  const hiddenManualContours = useMemo(() => new Set<string>(), [scope, volumeId, enabled]);
  useEffect(() => {
    if (!enabled || active || !ready || !volumeId || !data) return;
    const declinedIds = new Set(
      data.annotations
        .filter(
          (annotation) =>
            !annotation.attributes?.ai_prediction_condition &&
            annotation.attributes?.manual_annotation_review === "declined",
        )
        .map((annotation) => annotation.annotation_id),
    );
    const tools = getCornerstoneTools();
    const liveUids = new Set<string>();
    let changed = false;
    for (const annotation of tools.annotation.state.getAllAnnotations?.() ?? []) {
      const contour = annotation as SavedContourAnnotation;
      const uid = contour.annotationUID;
      if (!uid || contour.data?.segmentation?.segmentationId !== getContourSegmentationId(volumeId))
        continue;
      liveUids.add(uid);
      const hidden =
        typeof contour.data.savedAnnotationId === "string" &&
        declinedIds.has(contour.data.savedAnnotationId);
      const restoredWhileEditing =
        hidden && tools.annotation.visibility?.isAnnotationVisible?.(uid) === true;
      if (hidden === hiddenManualContours.has(uid) && !restoredWhileEditing) continue;
      tools.annotation.visibility?.setAnnotationVisibility(uid, !hidden);
      if (hidden) hiddenManualContours.add(uid);
      else hiddenManualContours.delete(uid);
      changed = true;
    }
    for (const uid of hiddenManualContours) {
      if (!liveUids.has(uid)) hiddenManualContours.delete(uid);
    }
    if (changed)
      (getCornerstone().getRenderingEngines?.() ?? []).forEach((engine) => engine.render());
  }, [enabled, active, ready, volumeId, data, contourRevision, hiddenManualContours]);
  // Query wrappers and callbacks can change identity on unrelated renders.
  const callbacks = useRef({ prepareIndices, onLoaded });
  callbacks.current = { prepareIndices, onLoaded };
  const snapshot = enabled ? JSON.stringify(jobsFor(data)) : "[]";
  // Keep identity reconciliation independent from load jobs: the final saved
  // annotation can be deleted at the same time as mask_url becomes null.
  const annotationIdsSnapshot =
    enabled && data
      ? JSON.stringify(data.annotations.map((annotation) => annotation.annotation_id))
      : null;
  useEffect(() => {
    if (!enabled || active || !ready || !volumeId || annotationIdsSnapshot == null) return;
    const jobs: Job[] = JSON.parse(snapshot);
    const savedAnnotationIds = new Set<string>(JSON.parse(annotationIdsSnapshot));
    const controller = new AbortController();
    const { signal } = controller;

    // The refreshed annotation query is authoritative. Candidate Decline and
    // Undo accept delete the saved annotation, so remove only contours carrying
    // that now-absent identity. Manual contours have no savedAnnotationId and
    // contours from another mounted volume have a different segmentationId.
    let removedContour = false;
    try {
      const cornerstoneTools = getCornerstoneTools();
      const annotationState = cornerstoneTools.annotation.state;
      const unregister =
        cornerstoneTools.utilities?.contourSegmentation?.removeContourSegmentationAnnotation;
      const contourSegmentationId = getContourSegmentationId(volumeId);
      const annotations = annotationState.getAllAnnotations?.() ?? [];
      for (const annotation of annotations) {
        if (annotation?.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
        const contour = annotation as SavedContourAnnotation;
        if (contour.data.segmentation?.segmentationId !== contourSegmentationId) continue;
        const savedAnnotationId = contour.data.savedAnnotationId;
        if (typeof savedAnnotationId !== "string" || savedAnnotationIds.has(savedAnnotationId)) {
          continue;
        }
        const annotationUid = annotation.annotationUID;
        if (typeof annotationUid !== "string") continue;
        try {
          cornerstoneTools.annotation.visibility?.setAnnotationVisibility?.(annotationUid, true);
        } catch {
          // Visibility cleanup is best effort; state removal below is authoritative.
        }
        try {
          unregister?.(contour);
        } catch {
          // Registration may already be gone; still remove the annotation state.
        }
        try {
          annotationState.removeAnnotation(annotationUid);
          removedContour = true;
        } catch {
          // Cornerstone may already have cleared global annotation state.
        }
      }
    } catch (error) {
      console.warn("[MaskLoad] Saved annotation reconciliation failed", error);
    }

    // A removed identity must be loadable again if a later decision recreates
    // it. Completion keys are JSON tuples whose first item is annotation_id.
    for (const key of completed) {
      try {
        const [annotationId] = JSON.parse(key);
        if (typeof annotationId === "string" && !savedAnnotationIds.has(annotationId)) {
          completed.delete(key);
        }
      } catch {
        completed.delete(key);
      }
    }
    if (removedContour) callbacks.current.onLoaded();

    async function load(job: Job): Promise<boolean> {
      const missing = job.targets.filter((target) => !completed.has(target.key));
      if (!missing.length) return true;
      return new Promise<boolean>((resolve) => {
        let attempts = 0;
        const stop = () => {
          clearInterval(poll);
          signal.removeEventListener("abort", abort);
        };
        const abort = () => {
          stop();
          resolve(false);
        };
        const poll = setInterval(async () => {
          attempts++;
          try {
            const cornerstone = getCornerstone();
            const engines = cornerstone.getRenderingEngines?.() ?? [];
            const viewport = engines
              .flatMap((engine) => engine.getViewports())
              .find(
                (vp: any) =>
                  vp.element &&
                  vp.getImageData?.()?.imageData &&
                  // Wait for the full volume; the progressive low-res preview has another grid.
                  !vp.getImageIds?.()[0]?.startsWith("lowresprev:") &&
                  (cornerstone.metaData.get("generalSeriesModule", vp.getImageIds?.()[0])
                    ?.seriesInstanceUID === job.series ||
                    vp.getImageIds?.()[0]?.includes(job.series)),
              ) as any;
            if (!viewport) {
              if (attempts >= 50) {
                stop();
                // The mask is fine; its series just isn't in any viewport (ENG-248).
                logger.warn("[MaskLoad] saved_mask_series_not_mounted", {
                  seriesUid: job.series,
                  annotationIds: job.targets.map((target) => target.id),
                });
                const series = savedMaskSeriesLabel(job.series, studyMetadataRef.current);
                emit("toast", {
                  message: `A saved mask is on ${series}, which isn't open in any viewport. To see it, reopen the case and choose that series.`,
                });
                resolve(false);
              }
              return;
            }
            clearInterval(poll);
            const localIndicesByStoredSegment = callbacks.current.prepareIndices();
            const currentIndex = viewport.getCurrentImageIdIndex?.() ?? 0;
            await loadMaskAsContours(job.url, volumeId!, viewport, viewport.element, {
              annotationIdsBySegment: new Map(missing.map((target) => [target.index, target.id])),
              includedStoredSegmentIndices: new Set(missing.map((target) => target.index)),
              localIndicesByStoredSegment,
              skipExistingContours: true,
              signal,
              // Every label in the file, not only the missing ones: the whole file is reindexed.
              labelOrders: new Map(job.targets.map((target) => [target.index, target.order])),
            });
            if (signal.aborted) return;
            missing.forEach((target) => completed.add(target.key));
            callbacks.current.onLoaded();
            // Viewport restoration is cosmetic; it must not invalidate a completed load.
            try {
              viewport.setImageIdIndex(currentIndex);
            } catch {
              /* viewport may have changed */
            }
            stop();
            resolve(true);
          } catch (error) {
            stop();
            if (!signal.aborted) {
              if (error instanceof SavedStonesOverlapError) {
                logger.warn("[MaskLoad] saved_mask_stones_overlap", {
                  seriesUid: job.series,
                  annotationIds: job.targets.map((target) => target.id),
                });
              } else {
                console.warn("[MaskLoad] Saved annotation load failed", error);
              }
              emit("toast", { message: savedMaskLoadFailureMessage(error) });
            }
            resolve(false);
          }
        }, 200);
        signal.addEventListener("abort", abort, { once: true });
      });
    }

    for (const primary of jobs.filter((job) => !job.parent)) {
      void load(primary).then((success) => {
        if (!success || signal.aborted) return;
        const parents = new Set(primary.targets.map((target) => target.key));
        for (const companion of jobs.filter((job) => job.parent && parents.has(job.parent))) {
          void load(companion);
        }
      });
    }
    return () => controller.abort();
  }, [enabled, active, ready, volumeId, completed, snapshot, annotationIdsSnapshot, revision]);
}
